import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { postPartyMovement, removePartyMovementBySource } from "@/lib/party-ledger";
import { isSupabaseConfigured, supabase } from "@/lib/supabase";
import type { VendorPaymentFormValues } from "@/features/vendor-payments/schema";
import type { PaymentMethod, VendorPaymentListRow } from "@/features/vendor-payments/types";

const text = (value: string | undefined) => value?.trim() || null;

function money(value: unknown) {
  const n = typeof value === "number" ? value : Number(value ?? 0);
  return Number.isNaN(n) ? 0 : n;
}

function hydrate(raw: Record<string, unknown>): VendorPaymentListRow {
  const vendor = raw.vendors as { code?: string; name?: string } | null;
  const po = raw.purchase_orders as { po_number?: string } | null;
  return {
    id: String(raw.id),
    vendor_id: String(raw.vendor_id),
    purchase_order_id: (raw.purchase_order_id as string | null) ?? null,
    payment_date: String(raw.payment_date),
    amount: money(raw.amount),
    payment_method: raw.payment_method as PaymentMethod,
    reference: (raw.reference as string | null) ?? null,
    notes: (raw.notes as string | null) ?? null,
    created_by: (raw.created_by as string | null) ?? null,
    updated_by: (raw.updated_by as string | null) ?? null,
    created_at: String(raw.created_at),
    updated_at: String(raw.updated_at),
    vendor_code: vendor?.code ?? null,
    vendor_name: vendor?.name ?? null,
    po_number: po?.po_number ?? null,
  };
}

async function syncApPayment(payment: {
  id: string;
  vendor_id: string;
  amount: number;
  payment_date: string;
  reference: string | null;
  po_number?: string | null;
}) {
  await postPartyMovement({
    party: "vendor",
    partyId: payment.vendor_id,
    kind: "payment",
    amount: -Math.abs(payment.amount),
    movementDate: payment.payment_date,
    reference: payment.reference,
    notes: payment.po_number ? `Against ${payment.po_number}` : null,
    sourceType: "vendor_payment",
    sourceId: payment.id,
  });
}

export function vendorPaymentToForm(row: VendorPaymentListRow): VendorPaymentFormValues {
  return {
    vendor_id: row.vendor_id,
    purchase_order_id: row.purchase_order_id ?? "",
    payment_date: row.payment_date,
    amount: row.amount,
    payment_method: row.payment_method,
    reference: row.reference ?? "",
    notes: row.notes ?? "",
  };
}

export function useVendorPayments(filters?: { vendorId?: string }) {
  return useQuery({
    queryKey: ["vendor_payments", filters?.vendorId ?? "all"],
    enabled: isSupabaseConfigured,
    queryFn: async (): Promise<VendorPaymentListRow[]> => {
      let q = supabase
        .from("vendor_payments")
        .select("*, vendors(code, name), purchase_orders(po_number)")
        .order("payment_date", { ascending: false })
        .order("created_at", { ascending: false })
        .limit(300);
      if (filters?.vendorId) q = q.eq("vendor_id", filters.vendorId);
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []).map((row) => hydrate(row as Record<string, unknown>));
    },
  });
}

export function useUpsertVendorPayment() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({
      id,
      values,
    }: {
      id?: string;
      values: VendorPaymentFormValues;
    }) => {
      const amount = money(values.amount);
      if (amount <= 0) throw new Error("Amount must be greater than 0");

      let poNumber: string | null = null;
      if (text(values.purchase_order_id)) {
        const { data: po, error: poErr } = await supabase
          .from("purchase_orders")
          .select("id, vendor_id, po_number")
          .eq("id", values.purchase_order_id)
          .single();
        if (poErr) throw poErr;
        if (String(po.vendor_id) !== values.vendor_id) {
          throw new Error("PO belongs to a different vendor");
        }
        poNumber = String(po.po_number);
      }

      const payload = {
        vendor_id: values.vendor_id,
        purchase_order_id: text(values.purchase_order_id),
        payment_date: values.payment_date,
        amount,
        payment_method: values.payment_method,
        reference: text(values.reference),
        notes: text(values.notes),
        updated_by: user?.id ?? null,
        updated_at: new Date().toISOString(),
      };

      let row: VendorPaymentListRow;
      if (id) {
        const { data, error } = await supabase
          .from("vendor_payments")
          .update(payload)
          .eq("id", id)
          .select("*, vendors(code, name), purchase_orders(po_number)")
          .single();
        if (error) throw error;
        row = hydrate(data as Record<string, unknown>);
      } else {
        const { data, error } = await supabase
          .from("vendor_payments")
          .insert({ ...payload, created_by: user?.id ?? null })
          .select("*, vendors(code, name), purchase_orders(po_number)")
          .single();
        if (error) throw error;
        row = hydrate(data as Record<string, unknown>);
      }

      await syncApPayment({
        id: row.id,
        vendor_id: row.vendor_id,
        amount: row.amount,
        payment_date: row.payment_date,
        reference: row.reference,
        po_number: poNumber ?? row.po_number,
      });

      return row;
    },
    onSuccess: async (_d, vars) => {
      await queryClient.invalidateQueries({ queryKey: ["vendor_payments"] });
      await queryClient.invalidateQueries({ queryKey: ["vendor_ap_movements"] });
      await queryClient.invalidateQueries({ queryKey: ["vendors"] });
      await queryClient.invalidateQueries({ queryKey: ["master_ledger"] });
      toast.success(vars.id ? "Payment updated" : "Payment recorded");
    },
    onError: (e: Error) => toast.error(e.message || "Failed to save payment"),
  });
}

export function useDeleteVendorPayment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (row: VendorPaymentListRow) => {
      await removePartyMovementBySource({
        party: "vendor",
        partyId: row.vendor_id,
        sourceType: "vendor_payment",
        sourceId: row.id,
      });
      const { error } = await supabase.from("vendor_payments").delete().eq("id", row.id);
      if (error) throw error;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["vendor_payments"] });
      await queryClient.invalidateQueries({ queryKey: ["vendor_ap_movements"] });
      await queryClient.invalidateQueries({ queryKey: ["vendors"] });
      await queryClient.invalidateQueries({ queryKey: ["master_ledger"] });
      toast.success("Payment deleted");
    },
    onError: (e: Error) => toast.error(e.message || "Failed to delete payment"),
  });
}
